<template>
  <section
    class="sec discover reveal"
    id="entdecken"
    data-ga-section="more_about_viluu"
  >
    <div class="dc-head">
      <span class="sec-eyebrow">{{ trans.get('landing.discover.eyebrow') }}</span>
      <h2 class="sec-title">
        {{ trans.get('landing.discover.title_prefix')
        }}<span class="grad">{{ trans.get('landing.discover.title_grad') }}</span
        ><span class="title-dot">{{ trans.get('landing.discover.title_dot') }}</span>
      </h2>
      <p class="sec-lede">{{ trans.get('landing.discover.lede') }}</p>
    </div>

    <div class="dc-cards">
      <a
        v-for="card in cards"
        :key="card.key"
        :href="card.href"
        class="dc-card"
        data-ga-content-link
        data-ga-section="more_about_viluu"
      >
        <div class="dc-ic">
          <component
            :is="card.icon"
            :size="24"
          />
        </div>
        <h3>{{ trans.get(card.titleKey) }}</h3>
        <p>{{ trans.get(card.textKey) }}</p>
        <span class="dc-arr">
          {{ trans.get(card.ctaKey) }}
          <icon-arrow-right :size="14" />
        </span>
      </a>
    </div>

    <div
      class="dc-cities"
      id="staedte"
      data-ga-section="cities"
    >
      <h3 class="dc-cities-title">
        {{ trans.get('landing.discover.cities_title_prefix')
        }}<span class="grad">{{ trans.get('landing.discover.cities_title_grad') }}</span
        ><span class="title-dot">{{ trans.get('landing.discover.cities_title_dot') }}</span>
      </h3>
      <div
        v-for="region in regions"
        :key="region.key"
        class="dc-region"
      >
        <div class="dc-region-head">
          <span class="dc-flag"><component :is="region.flag" /></span>
          <h4>{{ trans.get(region.nameKey) }}</h4>
        </div>
        <div class="dc-city-grid">
          <a
            v-for="city in region.cities"
            :key="city.slug"
            :href="`/dating-${city.slug}`"
            :data-city="city.slug"
            :data-country="region.key"
          >
            <span>{{ trans.get(cityTitleKey(city.slug)) }}</span
            ><span class="arr">→</span>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import IconCircleQuestion from '../icons/IconCircleQuestion.vue';
import IconHeart from '../icons/IconHeart.vue';
import IconMapMarker from '../icons/IconMapMarker.vue';
import IconArrowRight from '../icons/IconArrowRight.vue';
import FlagDe from '../icons/FlagDe.vue';
import FlagAt from '../icons/FlagAt.vue';
import FlagCh from '../icons/FlagCh.vue';
import { CITY_REGIONS, cityTitleKey } from '../../../views/pages/LandingV2/subpages/cities';

const CARD_ICONS = {
  c1: IconCircleQuestion,
  c2: IconHeart,
  c3: IconMapMarker,
};

const FLAGS = {
  de: FlagDe,
  at: FlagAt,
  ch: FlagCh,
};

export default {
  name: 'DiscoverSection',
  components: { IconArrowRight },
  data() {
    return {
      cards: [
        {
          key: 'c1',
          icon: CARD_ICONS.c1,
          href: 'so-funktioniert',
          titleKey: 'landing.discover.card1_title',
          textKey: 'landing.discover.card1_text',
          ctaKey: 'landing.discover.card1_cta',
        },
        {
          key: 'c2',
          icon: CARD_ICONS.c2,
          href: 'ueber-uns',
          titleKey: 'landing.discover.card2_title',
          textKey: 'landing.discover.card2_text',
          ctaKey: 'landing.discover.card2_cta',
        },
        {
          key: 'c3',
          icon: CARD_ICONS.c3,
          href: '#staedte',
          titleKey: 'landing.discover.card3_title',
          textKey: 'landing.discover.card3_text',
          ctaKey: 'landing.discover.card3_cta',
        },
      ],
      regions: CITY_REGIONS.map((region) => ({
        ...region,
        flag: FLAGS[region.key],
        cities: region.slugs.map((slug) => ({ slug })),
      })),
    };
  },
  methods: {
    cityTitleKey,
  },
};
</script>
<style scoped lang="scss">
.dc-head {
  max-width: 680px;
  margin: 0 0 48px;
}

.dc-head .sec-title {
  margin: 0 0 16px;
  text-wrap: balance;
}

.dc-head .sec-lede {
  margin: 0;
  text-wrap: pretty;
}

.dc-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  margin-bottom: 64px;
}

.dc-card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 24px;
  padding: 28px 28px 26px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 14px 32px -18px rgba(26, 12, 20, 0.15);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.dc-card:hover {
  transform: translateY(-4px);
  border-color: rgba(198, 62, 124, 0.35);
  box-shadow: 0 24px 50px -18px rgba(198, 62, 124, 0.3);
}

.dc-card .dc-ic {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #fff5ec, #ffe2d4);
  color: var(--pink);
  display: grid;
  place-items: center;
  margin-bottom: 6px;
  box-shadow: inset 0 -2px 4px rgba(198, 62, 124, 0.08);
}

.dc-card h3 {
  font-family: var(--display);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.015em;
  margin: 0;
  color: var(--ink);
  line-height: 1.2;
  text-wrap: balance;
}

.dc-card p {
  font-size: 14px;
  line-height: 1.55;
  color: var(--ink-dim);
  margin: 0 0 6px;
  text-wrap: pretty;
}

.dc-card .dc-arr {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--pink);
  font-weight: 600;
  font-size: 13.5px;
  letter-spacing: -0.005em;
}

.dc-card .dc-arr svg {
  transition: transform 0.2s ease;
}

.dc-card:hover .dc-arr svg {
  transform: translateX(4px);
}

.dc-cities-title {
  font-family: var(--display);
  font-size: clamp(26px, 3.4vw, 36px);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin: 0 0 32px;
  text-wrap: balance;
  color: var(--ink);
}

.dc-region {
  margin-bottom: 36px;
}

.dc-region:last-child {
  margin-bottom: 0;
}

.dc-region-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 10px 16px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 14px;
  width: fit-content;
  box-shadow: 0 4px 10px -6px rgba(20, 5, 15, 0.08);
}

.dc-region-head .dc-flag {
  width: 26px;
  height: 18px;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(20, 5, 15, 0.1);
}

.dc-region-head .dc-flag svg {
  width: 100%;
  height: 100%;
  display: block;
}

.dc-region-head h4 {
  font-family: var(--display);
  font-weight: 700;
  font-size: 18px;
  margin: 0;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.dc-city-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.dc-city-grid a {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 12px;
  color: var(--ink);
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
}

.dc-city-grid a:hover {
  border-color: var(--pink);
  color: var(--pink);
  transform: translateY(-2px);
  box-shadow: 0 10px 20px -10px rgba(198, 62, 124, 0.3);
}

.dc-city-grid a .arr {
  color: var(--pink);
  font-weight: 700;
  opacity: 0.5;
  transition: all 0.2s ease;
}

.dc-city-grid a:hover .arr {
  opacity: 1;
  transform: translateX(2px);
}

@media (max-width: 1100px) {
  .dc-cards {
    grid-template-columns: 1fr;
    gap: 14px;
  }
  .dc-city-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 560px) {
  .dc-city-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .dc-region-head h4 {
    font-size: 16px;
  }
}
</style>
